/* eslint-disable complexity */
import React, { useMemo } from "react";
import labelsAndHelpText from "protocol-setup/constants/text";

/**
 * Custom hook to get labels and help text from the centralized JSON file
 * @param {string} section - The main section (e.g., 'openRave', 'regulatory', 'browser')
 * @param {string} form - The form name (e.g., 'infoForm', 'raveSetupForm')
 * @returns {function} - Function to get label and help text for a field
 */
export const useLabelsAndHelpText = (section, form) => {
  const getFieldData = useMemo(() => {
    return (fieldName) => {
      const sectionData = labelsAndHelpText[section];
      if (!sectionData) {

        return { label: fieldName, helpText: null, labelHelp: null };
      }

      const formData = sectionData[form];
      if (!formData) {

        return { label: fieldName, helpText: null, labelHelp: null };
      }

      // Handle nested/dotted field names like "items.0.name" or "address.street"
      if (fieldName?.includes('.')) {

        const parts = fieldName.split('.').map(element =>/^\d+$/.test(element)? 'fields':element);
        const fieldData = parts.reduce((acc, key) => acc?.[key], formData)
        if (!fieldData) {
          return { label: fieldName, helpText: null, labelHelp: null };
        }

        return {
          label: fieldData.label || fieldName,
          helpText: fieldData.helpText || fieldData.helperText || null,
          labelHelp: fieldData.labelHelp || null
        };
      }

      const fieldData = formData[fieldName];
      if (!fieldData) {

        return { label: fieldName, helpText: null, labelHelp: null };
      }

      return {
        label: fieldData.label || fieldName,
        helpText: fieldData.helpText || fieldData.helperText || null,
        labelHelp: fieldData.labelHelp || null
      };
    };
  }, [section, form]);

  return getFieldData;
};

export function text(params, ...rest) {
  const path = params.split(".");
  let str = path.reduce((acc, key) => acc?.[key], labelsAndHelpText);
  str = str || "";

  const values = [...rest];
  if (values && values.length > 0) {
    return values.reduce((acc, val, i) => acc.replace(`$_${i + 1}`, val), str);
  }
  return str;
}


/**
 * Enhanced form components that automatically use labels and help text from JSON
 */
export const withLabelsAndHelpText = (Component, section, form) => {
  return (props) => {
    const { name, label, helpText, labelHelp, ...restProps } = props;
    const getFieldData = useLabelsAndHelpText(section, form);
    const fieldData = getFieldData(name);
    // Override props with JSON data if not explicitly provided
    const enhancedProps = {
      ...restProps,
      name,
      label: label || fieldData.label,
      helpText: helpText || fieldData.helpText,
      labelHelp: labelHelp || fieldData.labelHelp
    };

    return <Component {...enhancedProps} />;
  };
};

export default useLabelsAndHelpText;
